/* eslint-disable react/prop-types */
import React from "react";
import {
  Button,
  Flex,
  H1,
  H2,
  H3,
  H4,
  Loading,
  StyledLink,
} from "orcs-design-system";
import { Link } from "react-router-dom";
import { domToReact, attributesToProps } from "html-react-parser";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
// eslint-disable-next-line import/no-cycle
import AICharts from "./common-components/AICharts";
import { extractEChartJsonFromProps } from "./utils/extractEChartJsonFromProps";

const StyledUl = styled.ul`
  margin: 0;
  padding: ${themeGet("space.s")};
  display: flex;
  flex-direction: column;
  gap: ${themeGet("space.s")};
  li {
    margin: 0;
    margin-left: ${themeGet("space.r")};
    padding: 0;
  }
`;

const makeChartWithLoading = ({ generating }) => {
  return ({ children, attribs }) => {
    const props = attributesToProps(attribs);

    return generating ? (
      <Flex justifyContent="center">
        <Button as={Link} mt="l" mb="l" disabled={true} x>
          <Flex alignItems="center" gap="s">
            <Loading variant="button" />
            Generating graph...
          </Flex>
        </Button>
      </Flex>
    ) : (
      <AICharts specs={extractEChartJsonFromProps({ ...props, children })} />
    );
  };
};

export const defaultHTMLElementOverrides = ({
  workspaceId,
  enableAIDashboards = true,
  chatId,
  generating = false,
}) => ({
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  "final-response": ({ children, options }) => {
    return <div>{domToReact(children, options)}</div>;
  },

  dashboard: ({ attribs }) => {
    if (!enableAIDashboards) {
      return null;
    }

    const { id, title } = attributesToProps(attribs);
    const encodedTitle = encodeURIComponent(title);
    const dashboardUrl = `/${workspaceId}/dashboards/${chatId}/${
      id || encodedTitle
    }`;

    return (
      <Flex justifyContent="center">
        <Button as={Link} mt="l" mb="l" disabled={generating} to={dashboardUrl}>
          {generating ? (
            <Flex alignItems="center" gap="s">
              <Loading variant="button" />
              Generating dashboard...
            </Flex>
          ) : (
            "Go to dashboard"
          )}
        </Button>
      </Flex>
    );
  },

  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  h1: ({ children, options }) => {
    return <H1>{domToReact(children, options)}</H1>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  h2: ({ children, options }) => {
    return <H2>{domToReact(children, options)}</H2>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  h3: ({ children, options }) => {
    return <H3>{domToReact(children, options)}</H3>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  h4: ({ children, options }) => {
    return <H4>{domToReact(children, options)}</H4>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  ul: ({ children, options }) => {
    return <StyledUl>{domToReact(children, options)}</StyledUl>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  ol: ({ children, options }) => {
    return <StyledUl>{domToReact(children, options)}</StyledUl>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  a: ({ children, options, attribs }) => {
    const props = attributesToProps(attribs);
    return <StyledLink {...props}>{domToReact(children, options)}</StyledLink>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  p: ({ children, options }) => {
    return <p>{domToReact(children, options)}</p>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  graph: makeChartWithLoading({ generating }),
  "e-chart": makeChartWithLoading({ generating }),
});

/**
 * @param {string} workspaceId
 * @param {string} chatId
 * @param {Object} history - React Router history object
 * @param {Object} [htmlElementOverrides={}]
 * @param {boolean} [enableAIDashboards=true]
 * @param {boolean} [generating=false]
 * @returns {Object}
 */
export const createHtmlParsingOptions = (
  workspaceId,
  chatId,
  history,
  htmlElementOverrides = {},
  enableAIDashboards = true,
  generating = false
) => ({
  /**
   * @param {Object} param0
   * @param {Object} param0.attribs
   * @param {React.ReactNode} param0.children
   * @param {string} param0.tagName
   * @returns {React.ReactElement|undefined}
   */
  replace({ attribs, children, tagName }) {
    const override =
      htmlElementOverrides[tagName] ||
      defaultHTMLElementOverrides({
        workspaceId,
        chatId,
        history,
        enableAIDashboards,
        generating,
      })[tagName] ||
      (() => {});

    return override({
      options: createHtmlParsingOptions(
        workspaceId,
        chatId,
        history,
        htmlElementOverrides,
        enableAIDashboards,
        generating
      ),
      children,
      tagName,
      attribs,
    });
  },
});
